/**
 * Vertragsmanagement — kundenzentrierte Vertragsübersicht.
 *
 * Verträge/Lizenzen werden pro Deployment (Tenant) geführt, aber kommerziell
 * auf Kundenebene gebündelt. Diese Seite ist der navigierbare Einstieg ins
 * Vertragsmanagement: sie listet alle Kunden mit Deployment-Anzahl und
 * verlinkt in die Kunden-Detailseite, wo die gebündelten Verträge/Lizenzen
 * (Aggregation über die Deployments) gepflegt/eingesehen werden.
 *
 * Backend: GET /api/customers (customer.routes.ts).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomerView, listCustomers, extractApiError } from '../services/api';
import { labelForCustomerStatus } from '../services/status-labels';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-slate-200 text-slate-600',
};

export function ContractsOverviewPage() {
  const [rows, setRows] = useState<CustomerView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listCustomers()
      .then(setRows)
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Vertragsmanagement</h2>
        <p className="text-sm text-slate-500">
          Verträge &amp; Lizenzen werden pro Deployment geführt und je Kunde
          gebündelt. Öffne einen Kunden, um die zusammengefassten Verträge,
          Storage-/AI-Token-Quotas und Seats zu sehen.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3">Kunde</th>
              <th className="p-3">USt-ID</th>
              <th className="p-3">Marke</th>
              <th className="p-3">Status</th>
              <th className="p-3">Deployments</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{c.companyName}</td>
                <td className="p-3 font-mono text-slate-600">{c.vatId ?? '—'}</td>
                <td className="p-3 font-mono text-slate-600">{c.brand}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[c.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {labelForCustomerStatus(c.status)}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{c.tenantCount}</td>
                <td className="p-3">
                  <Link to={`/customers/${c.id}`} className="text-primary-600 hover:underline">Verträge ansehen →</Link>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">
                Noch keine Kunden angelegt.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
